/**
 * Contact-policy review and contact-work action forms.
 */
import { escapeAttr, escapeHtml, labelize, pill } from "./dashboard-html.js";
import { getDashboardRole } from "./dashboard-auth.js";
import type { ContactPolicyUiDecision, LeadFactoryTask } from "./dashboard-lead-factory-types.js";

const leadFactoryOutreachChannels = ["phone", "letter", "email", "skip_trace", "pi", "note"] as const;
const leadFactoryOutreachOutcomes = [
  "attempted",
  "connected",
  "interested",
  "left_message",
  "sent",
  "returned_mail",
  "bad_contact",
  "needs_follow_up",
  "not_interested",
  "do_not_contact",
  "pi_needed",
  "skip_trace_needed"
] as const;
const skipTraceResultStatuses = ["phone_found", "email_found", "mailing_address_found", "no_contact_found", "owner_deceased", "needs_pi_review"] as const;
const piResultStatuses = ["owner_located", "death_confirmed", "heir_identified", "probate_found", "no_owner_contact", "needs_skip_trace"] as const;
const contactPolicyOverrideRoles = ["manager", "admin", "automation"] as const;
const contactPolicyTimezone = "America/New_York";
const contactPolicyAllowedDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const contactPolicyStartHour = 9;
const contactPolicyEndHour = 20;

function optionHtml(value: string, selected: string): string {
  return `<option value="${escapeAttr(value)}" ${value === selected ? "selected" : ""}>${escapeHtml(labelize(value))}</option>`;
}

function defaultFutureDatetimeLocal(daysFromNow: number): string {
  return new Date(Date.now() + daysFromNow * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
}

export function followUpTaskHtml(task: LeadFactoryTask): string {
  const leadLabel = task.property_address || task.owner_name || task.property_lead_id || "Follow-up lead";
  return `
    <article class="task-item">
      <div>
        <strong>${escapeHtml(leadLabel)}</strong>
        <small>${escapeHtml(task.follow_up_at || task.created_at)} · ${escapeHtml(task.last_channel || "outreach")} · ${escapeHtml(task.last_outcome || "follow up")}</small>
        <p>${escapeHtml(task.instructions)}</p>
      </div>
      ${task.property_lead_id ? followUpScheduleFormHtml(task) : `<button class="button secondary" data-task-complete="${escapeAttr(task.id)}">Complete</button>`}
    </article>
  `;
}

export function contactWorkActionFormHtml(task: LeadFactoryTask): string {
  const defaults = defaultOutreachForTask(task);
  return `
    <form class="structured-action-form contact-work-form" data-contact-outreach-form data-contact-work-form data-lead-id="${escapeAttr(task.property_lead_id || "")}" data-contact-task-id="${escapeAttr(task.id)}" data-lead-status="${escapeAttr(task.lead_status || "")}" data-contact-readiness="${escapeAttr(task.contact_readiness || "")}">
      <select aria-label="Contact work channel" data-contact-outreach-channel data-contact-work-channel>
        ${leadFactoryOutreachChannels.map((channel) => optionHtml(channel, defaults.channel)).join("")}
      </select>
      <select aria-label="Contact work outcome" data-contact-outreach-outcome data-contact-work-outcome>
        ${leadFactoryOutreachOutcomes.map((outcome) => optionHtml(outcome, defaults.outcome)).join("")}
      </select>
      <input type="datetime-local" aria-label="Contact work follow-up" value="${escapeAttr(defaults.followUpValue)}" data-contact-outreach-follow-up data-contact-work-follow-up>
      ${contactPolicyReviewHtml(task.lead_status || "", task.contact_readiness || "", defaults.channel, defaults.outcome, defaults.policyOverrideReason, "data-contact-work-policy")}
      ${specialtyResultCaptureHtml(task)}
      <input type="text" maxlength="160" value="${escapeAttr(defaults.note)}" aria-label="Contact work note" data-contact-outreach-note data-contact-work-note>
      <button class="button secondary" type="submit">${escapeHtml(defaults.buttonLabel)}</button>
    </form>
  `;
}

export function specialtyResultCaptureHtml(task: LeadFactoryTask): string {
  if (task.task_type === "skip_trace") {
    return `
      <div class="specialty-result-capture" data-specialty-result-capture data-specialty-result-type="skip_trace">
        <select aria-label="Skip trace result" data-specialty-result-status>
          ${skipTraceResultStatuses.map((status) => optionHtml(status, "phone_found")).join("")}
        </select>
        <input type="text" maxlength="120" aria-label="Skip trace source" data-specialty-result-source>
        <input type="text" maxlength="80" placeholder="919-555-0123" aria-label="Found phone" data-specialty-result-phone>
        <input type="email" maxlength="120" placeholder="owner@example.com" aria-label="Found email" data-specialty-result-email>
        <input type="text" maxlength="160" placeholder="Confirmed mailing address" aria-label="Found address" data-specialty-result-address>
        <input type="number" min="0" max="1" step="0.01" aria-label="Result confidence" data-specialty-result-confidence>
        <input type="text" maxlength="220" aria-label="Skip trace evidence" data-specialty-result-evidence>
      </div>
    `;
  }
  if (task.task_type === "pi_research") {
    return `
      <div class="specialty-result-capture" data-specialty-result-capture data-specialty-result-type="pi_research">
        <select aria-label="PI result" data-specialty-result-status>
          ${piResultStatuses.map((status) => optionHtml(status, "owner_located")).join("")}
        </select>
        <input type="text" maxlength="120" aria-label="PI source" data-specialty-result-source>
        <input type="text" maxlength="120" placeholder="Owner/heir name" aria-label="PI subject" data-specialty-result-contact-name>
        <input type="text" maxlength="120" placeholder="Probate/heir/death evidence" aria-label="PI finding" data-specialty-result-finding>
        <input type="number" min="0" max="1" step="0.01" aria-label="PI confidence" data-specialty-result-confidence>
        <input type="text" maxlength="220" aria-label="PI evidence" data-specialty-result-evidence>
      </div>
    `;
  }
  return "";
}

export function followUpScheduleFormHtml(task: LeadFactoryTask): string {
  const followUpValue = defaultFutureDatetimeLocal(3);
  const followUpChannel = defaultFollowUpChannelForTask(task);
  return `
    <form class="structured-action-form follow-up-schedule-form" data-contact-outreach-form data-follow-up-schedule-form data-lead-id="${escapeAttr(task.property_lead_id || "")}" data-contact-task-id="${escapeAttr(task.id)}" data-lead-status="${escapeAttr(task.lead_status || "")}" data-contact-readiness="${escapeAttr(task.contact_readiness || "")}">
      <select aria-label="Follow-up channel" data-contact-outreach-channel data-follow-up-channel>
        ${leadFactoryOutreachChannels.map((channel) => optionHtml(channel, followUpChannel)).join("")}
      </select>
      <select aria-label="Follow-up outcome" data-contact-outreach-outcome data-follow-up-outcome>
        ${leadFactoryOutreachOutcomes.map((outcome) => optionHtml(outcome, "needs_follow_up")).join("")}
      </select>
      <input type="datetime-local" aria-label="Next follow-up at" value="${escapeAttr(followUpValue)}" data-contact-outreach-follow-up data-follow-up-at>
      ${contactPolicyReviewHtml(task.lead_status || "", task.contact_readiness || "", followUpChannel, "needs_follow_up", followUpChannel === "phone" ? "manager_approved_phone_follow_up" : "", "data-follow-up-policy")}
      <input type="text" maxlength="160" value="Dashboard scheduled next follow-up." aria-label="Follow-up note" data-contact-outreach-note data-follow-up-note>
      <button class="button secondary" type="submit">Schedule follow-up</button>
    </form>
  `;
}

export function defaultFollowUpChannelForTask(task: LeadFactoryTask): string {
  const readiness = task.contact_readiness || "";
  const leadStatus = task.lead_status || "";
  const candidate = task.last_channel && leadFactoryOutreachChannels.includes(task.last_channel as (typeof leadFactoryOutreachChannels)[number])
    ? task.last_channel
    : "";
  if (candidate) {
    const decision = evaluateDashboardContactPolicy(candidate, "needs_follow_up", leadStatus, readiness, new Date());
    if (decision.allowed) return candidate;
  }
  const preferredChannels =
    readiness === "ready_multi_channel"
      ? ["phone", "letter"]
      : readiness === "ready_for_call"
        ? ["phone"]
        : readiness === "ready_for_letter"
          ? ["letter"]
          : readiness === "needs_skip_trace"
            ? ["skip_trace"]
            : readiness === "needs_pi"
              ? ["pi"]
              : [];
  for (const channel of preferredChannels) {
    const decision = evaluateDashboardContactPolicy(channel, "needs_follow_up", leadStatus, readiness, new Date());
    if (decision.allowed) return channel;
  }
  return "note";
}

export function contactPolicyReviewHtml(
  leadStatus: string,
  contactReadiness: string,
  channel: string,
  outcome: string,
  defaultOverrideReason: string,
  reasonExtraAttribute: string
): string {
  const decision = evaluateDashboardContactPolicy(channel, outcome, leadStatus, contactReadiness, new Date());
  const defaultReason = decision.allowed ? "" : defaultOverrideReason || defaultPolicyOverrideReason(decision.reason);
  const canOverride = canOverrideContactPolicy();
  return `
    <div class="contact-policy-review ${decision.allowed ? "allowed" : "blocked"}" data-contact-policy-review>
      <div>
        <strong data-contact-policy-decision>${escapeHtml(contactPolicyDecisionLabel(decision))}</strong>
        <small data-contact-policy-summary>${escapeHtml(contactPolicySummary(decision))}</small>
      </div>
      <label class="contact-policy-ack ${decision.allowed ? "is-hidden" : ""}" data-contact-policy-ack-label>
        <input type="checkbox" ${decision.allowed || !canOverride ? "disabled" : ""} data-contact-policy-ack>
        Manager override approved
      </label>
      <input type="text" maxlength="100" value="${escapeAttr(defaultReason)}" aria-label="Policy override reason" data-contact-outreach-policy data-contact-policy-reason ${reasonExtraAttribute}>
      <small class="contact-policy-role" data-contact-policy-role>${escapeHtml(contactPolicyRoleSummary(decision))}</small>
    </div>
  `;
}

export function canOverrideContactPolicy(): boolean {
  return contactPolicyOverrideRoles.includes(getDashboardRole() as (typeof contactPolicyOverrideRoles)[number]);
}

export function evaluateDashboardContactPolicy(
  channel: string,
  outcome: string,
  leadStatus: string,
  contactReadiness: string,
  attemptedAt: Date
): ContactPolicyUiDecision {
  const localTime = contactPolicyLocalTime(attemptedAt);
  const base: ContactPolicyUiDecision = {
    allowed: true,
    reason: "allowed",
    channel,
    contactReadiness,
    localDay: localTime.day,
    localHour: localTime.hour,
    timezone: contactPolicyTimezone
  };
  if (["suppressed", "dead", "closed_lost"].includes(leadStatus) || contactReadiness === "contact_blocked") {
    return { ...base, allowed: false, reason: "contact_blocked" };
  }
  if (outcome === "do_not_contact") return { ...base, reason: "do_not_contact_block_allowed" };
  if (channel === "phone" && !["ready_for_call", "ready_multi_channel"].includes(contactReadiness)) {
    return { ...base, allowed: false, reason: "phone_contact_not_ready" };
  }
  if (["letter", "email"].includes(channel) && !["ready_for_letter", "ready_multi_channel"].includes(contactReadiness)) {
    return { ...base, allowed: false, reason: "written_contact_not_ready" };
  }
  if (channel === "skip_trace" && contactReadiness !== "needs_skip_trace") {
    return { ...base, allowed: false, reason: "skip_trace_not_required" };
  }
  if (channel === "pi" && contactReadiness !== "needs_pi") {
    return { ...base, allowed: false, reason: "pi_not_required" };
  }
  if (channel === "phone") {
    const allowedDay = contactPolicyAllowedDays.includes(localTime.day);
    const allowedHour = localTime.hour >= contactPolicyStartHour && localTime.hour < contactPolicyEndHour;
    if (!allowedDay || !allowedHour) return { ...base, allowed: false, reason: "outside_phone_contact_window" };
  }
  return base;
}

export function contactPolicyLocalTime(date: Date): { day: string; hour: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: contactPolicyTimezone,
    weekday: "long",
    hour: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const day = parts.find((part) => part.type === "weekday")?.value ?? "unknown";
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? "0");
  return { day, hour: Number.isFinite(hour) ? hour : 0 };
}

export function contactPolicyDecisionLabel(decision: ContactPolicyUiDecision): string {
  return decision.allowed ? "Policy allowed" : "Policy blocked";
}

export function contactPolicySummary(decision: ContactPolicyUiDecision): string {
  if (decision.allowed) return `${labelize(decision.channel)} outreach is allowed for ${labelize(decision.contactReadiness || "unknown readiness")}.`;
  if (decision.reason === "outside_phone_contact_window") {
    return `Phone contact is outside the ${contactPolicyStartHour}:00-${contactPolicyEndHour}:00 ${decision.timezone} window.`;
  }
  return `${labelize(decision.channel)} outreach is blocked: ${labelize(decision.reason)}.`;
}

export function contactPolicyRoleSummary(decision: ContactPolicyUiDecision): string {
  if (decision.allowed) return "No override required.";
  return canOverrideContactPolicy() ? "Manager/admin override requires approval and reason." : "Current role cannot override this policy block.";
}

export function defaultPolicyOverrideReason(reason: string): string {
  return `manager_approved_${reason}`;
}

export function defaultOutreachForTask(task: LeadFactoryTask): {
  channel: string;
  outcome: string;
  followUpValue: string;
  policyOverrideReason: string;
  note: string;
  buttonLabel: string;
} {
  if (task.task_type === "call_owner") {
    return {
      channel: "phone",
      outcome: "attempted",
      followUpValue: "",
      policyOverrideReason: "operator_policy_review",
      note: "Dashboard recorded owner call work.",
      buttonLabel: "Record call"
    };
  }
  if (task.task_type === "prepare_letter") {
    return {
      channel: "letter",
      outcome: "sent",
      followUpValue: "",
      policyOverrideReason: "",
      note: "Dashboard recorded owner letter work.",
      buttonLabel: "Record letter"
    };
  }
  if (task.task_type === "skip_trace") {
    return {
      channel: "skip_trace",
      outcome: "connected",
      followUpValue: "",
      policyOverrideReason: "",
      note: "Dashboard completed skip-trace work.",
      buttonLabel: "Complete skip trace"
    };
  }
  if (task.task_type === "pi_research") {
    return {
      channel: "pi",
      outcome: "connected",
      followUpValue: "",
      policyOverrideReason: "",
      note: "Dashboard completed PI research.",
      buttonLabel: "Complete PI"
    };
  }
  return {
    channel: "note",
    outcome: "attempted",
    followUpValue: "",
    policyOverrideReason: "",
    note: "Dashboard recorded contact work.",
    buttonLabel: "Record work"
  };
}

